import { logError } from "utility/Function";
import {
  ORI_CONVERSATION_STARTERS,
  OriConversationStarter,
} from "./oriConversationStarters";

// Namespaced by learner so two people sharing a browser keep separate progress.
const seenKey = (learnerId: string): string =>
  `wone.oriStarters.seen.${learnerId}`;

export function getSeenStarterIds(learnerId: string): string[] {
  try {
    const raw = localStorage.getItem(seenKey(learnerId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch (err) {
    logError("Failed to read seen Ori conversation starters", err as Error);
    return [];
  }
}

export function markStarterSeen(learnerId: string, id: string): void {
  try {
    const seen = getSeenStarterIds(learnerId);
    if (seen.includes(id)) return;
    localStorage.setItem(seenKey(learnerId), JSON.stringify([...seen, id]));
  } catch (err) {
    logError("Failed to persist seen Ori conversation starter", err as Error);
  }
}

// One random starter the learner hasn't seen yet, or null once all are seen.
export function pickUnseenStarter(
  learnerId: string
): OriConversationStarter | null {
  const seen = new Set(getSeenStarterIds(learnerId));
  const unseen = ORI_CONVERSATION_STARTERS.filter((s) => !seen.has(s.id));
  if (unseen.length === 0) return null;
  return unseen[Math.floor(Math.random() * unseen.length)];
}

// Resolved once per page load and cached for the lifetime of the JS runtime.
// The tip therefore stays fixed across client-side navigation (moving between
// pages within the SPA) and only one tip is consumed per session — a hard
// reload is a new runtime, so the next unseen tip is chosen. Marking the tip
// seen happens exactly once here, at resolution.
let session: {
  learnerId: string;
  starter: OriConversationStarter | null;
  impressionFired: boolean;
} | null = null;

export function resolveSessionStarter(
  learnerId: string
): OriConversationStarter | null {
  if (session?.learnerId === learnerId) return session.starter;
  const starter = pickUnseenStarter(learnerId);
  if (starter) markStarterSeen(learnerId, starter.id);
  session = { learnerId, starter, impressionFired: false };
  return starter;
}

// True exactly once per page load for the resolved learner, so the impression
// fires a single time even if the home page remounts during the session.
export function claimStarterImpression(learnerId: string): boolean {
  if (!session || session.learnerId !== learnerId || session.impressionFired) {
    return false;
  }
  session.impressionFired = true;
  return true;
}

// Test seam: drop the per-runtime cache so each test starts a fresh session.
export function resetSessionStarter(): void {
  session = null;
}
